import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brand } from "../components/Brand";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ImportProjectButton } from "../components/ImportProjectButton/ImportProjectButton";
import { ProjectThumbnail } from "../components/ProjectThumbnail";
import { useParentMode } from "../parent/ParentModeContext";
import type { PerfectDrawProject } from "../projects/types";
import { deleteProject, listProjects } from "../storage/db";

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export function HomeScreen() {
  const parent = useParentMode();
  const [projects, setProjects] = useState<PerfectDrawProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [projectToDelete, setProjectToDelete] = useState<PerfectDrawProject | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadLibrary = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      setProjects(await listProjects());
    } catch {
      setLoadError("We couldn’t open your project library. Please refresh and try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const openLibrary = async () => {
      try {
        const storedProjects = await listProjects();
        if (!cancelled) setProjects(storedProjects);
      } catch {
        if (!cancelled) {
          setLoadError("We couldn’t open your project library. Please refresh and try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void openLibrary();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDelete = async () => {
    if (!projectToDelete || isDeleting) return;
    if (!parent.can("deleteProject")) {
      setProjectToDelete(null);
      parent.askParent("Ask a parent before deleting a project.");
      return;
    }
    setIsDeleting(true);
    try {
      await deleteProject(projectToDelete.id);
      setProjects((current) => current.filter((project) => project.id !== projectToDelete.id));
      setProjectToDelete(null);
    } catch {
      setLoadError("That project couldn’t be deleted. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <main className="home-screen">
      <header className="home-header">
        <Brand />
        <div className="home-header__controls">
          {parent.isConfigured || parent.isCorrupt ? (
            <span className="child-mode-pill">
              {parent.isUnlocked ? "Parent Mode Active" : "Child Mode"}
            </span>
          ) : null}
          {parent.isUnlocked ? (
            <button className="button button--secondary" onClick={parent.lock} type="button">
              Return to Child Mode
            </button>
          ) : null}
          <Link className="button button--secondary" to="/parent">
            {parent.isUnlocked ? "Parent Settings" : "Parent Mode"}
          </Link>
          <div className="privacy-pill">
            <span aria-hidden="true">⌂</span>
            Your art stays on this device
          </div>
        </div>
      </header>

      {parent.notice ? (
        <div className="child-mode-notice" role="status">
          <span>{parent.notice}</span>
          <button
            aria-label="Dismiss Child Mode message"
            onClick={parent.clearNotice}
            type="button"
          >
            ×
          </button>
        </div>
      ) : null}

      <section className="hero" aria-labelledby="home-title">
        <div className="hero__copy">
          <p className="eyebrow">Your ideas, your way</p>
          <h1 id="home-title">What will you make today?</h1>
          <p>Pick up where you left off, or start a brand-new adventure.</p>
        </div>
        <div className="hero__actions" aria-label="Create a project">
          {parent.can("createDrawing") ? (
            <Link className="create-card create-card--drawing" to="/new?type=drawing">
              <span className="create-card__icon" aria-hidden="true">
                ✎
              </span>
              <span>
                <strong>New Drawing</strong>
                <small>A fresh blank canvas</small>
              </span>
              <span className="create-card__arrow" aria-hidden="true">
                →
              </span>
            </Link>
          ) : (
            <div
              aria-disabled="true"
              className="create-card create-card--drawing create-card--locked"
            >
              <span className="create-card__icon" aria-hidden="true">
                ⌁
              </span>
              <span>
                <strong>New Drawing</strong>
                <small>Ask a parent</small>
              </span>
            </div>
          )}
          {parent.can("createComic") ? (
            <Link className="create-card create-card--comic" to="/new?type=comic">
              <span className="create-card__icon" aria-hidden="true">
                ▦
              </span>
              <span>
                <strong>New Comic</strong>
                <small>Tell a story in panels</small>
              </span>
              <span className="create-card__arrow" aria-hidden="true">
                →
              </span>
            </Link>
          ) : (
            <div
              aria-disabled="true"
              className="create-card create-card--comic create-card--locked"
            >
              <span className="create-card__icon" aria-hidden="true">
                ⌁
              </span>
              <span>
                <strong>New Comic</strong>
                <small>Ask a parent</small>
              </span>
            </div>
          )}
          {parent.can("useActivities") ? (
            <Link className="create-card create-card--activities" to="/activities">
              <span className="create-card__icon" aria-hidden="true">
                ☆
              </span>
              <span>
                <strong>Drawing Activities</strong>
                <small>Learn and try creative ideas</small>
              </span>
              <span className="create-card__arrow" aria-hidden="true">
                →
              </span>
            </Link>
          ) : (
            <div
              aria-disabled="true"
              className="create-card create-card--activities create-card--locked"
            >
              <span className="create-card__icon" aria-hidden="true">
                ⌁
              </span>
              <span>
                <strong>Drawing Activities</strong>
                <small>Ask a parent</small>
              </span>
            </div>
          )}
        </div>
      </section>

      <section className="library" aria-labelledby="library-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Saved on this tablet</p>
            <h2 id="library-title">My projects</h2>
          </div>
          {!isLoading && !loadError && projects.length > 0 ? (
            <span className="project-count">
              {projects.length} {projects.length === 1 ? "project" : "projects"}
            </span>
          ) : null}
          <ImportProjectButton
            allowed={parent.can("importBackup")}
            onBlocked={() => parent.askParent("Ask a parent before importing a backup.")}
            onImported={(project) => {
              setLoadError("");
              setProjects((current) => [project, ...current]);
            }}
          />
        </div>

        {loadError ? (
          <div className="notice notice--error" role="alert">
            <p>{loadError}</p>
            <button
              className="button button--secondary"
              type="button"
              onClick={() => void loadLibrary()}
            >
              Try again
            </button>
          </div>
        ) : null}

        {isLoading ? (
          <div className="library-loading" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            <span>Opening your project shelf…</span>
          </div>
        ) : null}

        {!isLoading && !loadError && projects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__art" aria-hidden="true">
              <span className="empty-state__page" />
              <span className="empty-state__sun">+</span>
              <span className="empty-state__pencil">✎</span>
            </div>
            <div>
              <h3>Your project shelf is ready</h3>
              <p>Make your first drawing and it will wait for you right here.</p>
              {parent.can("createDrawing") ? (
                <Link className="button button--primary" to="/new?type=drawing">
                  Make my first drawing
                </Link>
              ) : (
                <button className="button button--secondary" disabled type="button">
                  Ask a parent to make a drawing
                </button>
              )}
            </div>
          </div>
        ) : null}

        {!isLoading && projects.length > 0 ? (
          <div className="project-grid">
            {projects.map((project) => (
              <article className="project-card" key={project.id}>
                <Link className="project-card__preview" to={`/studio/${project.id}`}>
                  <ProjectThumbnail
                    blob={project.thumbnailBlob}
                    orientation={project.orientation}
                  />
                  <span className="sr-only">Open {project.name}</span>
                </Link>
                <div className="project-card__body">
                  <span className={`type-badge type-badge--${project.type}`}>
                    {project.type === "comic" ? "Comic" : "Drawing"}
                  </span>
                  <h3>{project.name}</h3>
                  <p>
                    Edited {DATE_FORMATTER.format(new Date(project.updatedAt))} · {project.width} ×{" "}
                    {project.height}
                  </p>
                  <div className="project-card__actions">
                    <Link
                      className="button button--small button--secondary"
                      to={`/studio/${project.id}`}
                    >
                      Open
                    </Link>
                    {parent.can("deleteProject") ? (
                      <button
                        className="icon-button icon-button--danger"
                        type="button"
                        aria-label={`Delete ${project.name}`}
                        onClick={() => setProjectToDelete(project)}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </section>

      <footer className="home-footer">
        <span>Private by design</span>
        <span aria-hidden="true">•</span>
        <span>Works offline</span>
        <span aria-hidden="true">•</span>
        <span>No ads</span>
      </footer>

      {projectToDelete ? (
        <ConfirmDialog
          projectName={projectToDelete.name}
          isBusy={isDeleting}
          onCancel={() => setProjectToDelete(null)}
          onConfirm={() => void handleDelete()}
        />
      ) : null}
    </main>
  );
}
